import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BfiDataGrid, Column, MasterDetail } from '../../common/SharedComponents';
import { formatCurrency, formatPercent } from '../../common/SharedFunctions';
import { LoanStatusType, LoanType } from '../common/LoanConstants';
import {
    getLoanGridSettingId,
    onBorrowerCellRender,
    onCityCellRender,
    onStateCellRender,
    onActionCellRender,
    onRenderPortfolioGrid,
    onRowPrepared,
    OnOpenLoanDetail,
} from '../common/LoanFunctions';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { LoanRepaidIndicatorColumn } from '../common/components/LoanRepaidIndicatorColumn';
import { LoanApprovedOrNoteDateColumn } from '../common/components/LoanApprovedOrNoteDateColumn';
import { onStructureTypeCellRender } from '../common/functions/onStructureTypeCellRender';
import { onLoanTermCellRender } from '../common/functions/onLoanTermCellRender';

const onFundedDollarRender = (e) => formatCurrency(e.data.fundedTotal, 0);
const onFundedPercentRender = (e) =>
    formatPercent(e.data.promissoryNote ? (e.data.fundedTotal ?? 0) / e.data.promissoryNote : 0, 0);

const OutstandingConstructionTable = ({ gridStore, extensionNumber = -1 }) => {
    const searchState = useSelector((state) => state.searchState);
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const location = useLocation();

    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = (criteria) => {
        criteria.loanTypeId = LoanType.Construction;
        criteria.statusId = LoanStatusType.Outstanding;
        if (extensionNumber > -1) {
            criteria.extensionNumber = extensionNumber;
        }
        return loanApi.getGrid_StandardLoans(criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.loan);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const onPresoldRender = (e) => {
        return e.data.isPreSold ? 'Yes' : 'No';
    };

    if (!gridStore) {
        return null;
    }

    return (
        <BfiDataGrid
            id={`ot_construction_${extensionNumber}`}
            gridType={getLoanGridSettingId(LoanType.Construction, LoanStatusType.Outstanding)}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            onRowPrepared={onRowPrepared}
            onCellDblClick={(e) => OnOpenLoanDetail(e, location)}
        >
            <Column
                dataField='borrowerEntity'
                caption='Entity'
                cellRender={onBorrowerCellRender}
                name='borrowerEntity'
                minWidth={120}
            />
            <Column
                dataField='propertyCity'
                caption='City'
                cellRender={onCityCellRender}
                width={90}
            />
            <Column
                dataField='propertyState'
                caption='State'
                cellRender={onStateCellRender}
                width={70}
            />
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={80}
            />
            <Column
                dataField='loanNumber'
                caption='Loan #'
                dataType='string'
                width={80}
            />
            <Column
                dataField='structureTypeId'
                caption='Structure Type'
                dataType='string'
                cellRender={onStructureTypeCellRender}
                width={120}
            />
            {LoanCommitOrApprovalColumn({ statusId: LoanStatusType.Outstanding })}
            {LoanApprovedOrNoteDateColumn({ statusId: LoanStatusType.Outstanding })}
            <Column
                dataField='monthsSinceClosing'
                caption='Mnths'
                width={60}
                alignment={'left'}
            />
            <Column
                dataField='origMaturityDate'
                caption='Orig. Mat.'
                dataType='date'
                width={90}
            />
            <Column
                dataField='initialTermMonths'
                caption='Term'
                dataType='string'
                width={60}
                alignment={'left'}
                cellRender={onLoanTermCellRender}
            />
            <Column
                dataField='fundedTotal'
                caption='Funded'
                dataType='number'
                format='currency'
                width={90}
                alignment={'left'}
                cellRender={onFundedDollarRender}
            />
            <Column
                caption='Funded'
                dataType='number'
                width={70}
                alignment={'left'}
                cellRender={onFundedPercentRender}
                allowSorting={false}
                cssClass='no-sort-header'
            />
            <Column
                dataField='interestRate'
                caption='Rate'
                dataType='number'
                format={{ type: 'percent', precision: 2 }}
                width={70}
                alignment={'left'}
            />
            <Column
                dataField='loanToValue'
                caption='LTV'
                dataType='number'
                format={{ type: 'percent', precision: 1 }}
                width={80}
                alignment={'left'}
            />
            <Column
                dataField='isPreSold'
                caption='Pre-Sold'
                width={60}
                cellRender={onPresoldRender}
                alignment={'left'}
            />
            <Column
                dataField='pointOfContact'
                caption='POC'
                dataType='string'
                width={110}
            />
            <Column
                dataField='ownerName'
                caption='User'
                dataType='string'
                width={110}
            />
            <Column
                dataField='broker'
                dataType='string'
                width={110}
            />
            {LoanRepaidIndicatorColumn({ statusId: LoanStatusType.Outstanding })}
            <Column
                alignment='center'
                cellRender={onActionCellRender}
                width={50}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
                type='actions'
                fixed={true}
                fixedPosition={'right'}
            />
            <MasterDetail
                enabled={true}
                render={(e) => onRenderPortfolioGrid(e, LoanStatusType.Outstanding)}
            />
        </BfiDataGrid>
    );
};

export default OutstandingConstructionTable;
